import { useApolloClient } from '@apollo/client';
import { isNil } from 'lodash/fp';
import { useCallback } from 'react';
import { type OptionsBlockSpecsFragment } from '../../../../api/fragments/OptionsBlockSpecs';
import { useCreateOptionsBlockMutation } from '../../../../api/mutations/createOptionsBlock';
import { useDeleteOptionsBlockMutation } from '../../../../api/mutations/deleteOptionsBlock';
import { useUpdateOptionsBlockMutation } from '../../../../api/mutations/updateOptionsBlock';
import {
    GetVariantConfiguratorDocument,
    type GetVariantConfiguratorQuery,
    type GetVariantConfiguratorQueryVariables,
} from '../../../../api/queries/getVariantConfigurator';
import { BlockType, OptionKind } from '../../../../api/types';
import useHandleBlockAssetSave from './useHandleBlockAssetSave';
import { type OptionBlockValue } from './utils';

const useHandleBlockSave = () => {
    const apollo = useApolloClient();

    const [createBlockOptionMutation] = useCreateOptionsBlockMutation();
    const [updateBlockOptionMutation] = useUpdateOptionsBlockMutation();
    const [deleteBlockOptionMutation] = useDeleteOptionsBlockMutation();

    const { uploadAsset, deleteAsset, uploadOptionSettingAsset, deleteOptionSettingAsset } = useHandleBlockAssetSave();

    const createBlockOption = useCallback(
        async (variantConfiguratorId: string, optionBlockValue: OptionBlockValue) => {
            const { optionsBlockInput } = optionBlockValue;
            const { sectionImages, ...restOptionBlockInput } = optionsBlockInput;
            const { optionSettings } = restOptionBlockInput;
            const singleSelectOptionSettingsInput = optionSettings?.singleSelectOptionSettingsInput;

            const { data } = await createBlockOptionMutation({
                variables: {
                    settings: {
                        type: BlockType.Options,
                        optionsBlockInput: {
                            ...restOptionBlockInput,
                            optionSettings: {
                                ...optionSettings,
                                singleSelectOptionSettingsInput:
                                    optionSettings.type === OptionKind.SingleSelect
                                        ? {
                                              ...singleSelectOptionSettingsInput,
                                              options: singleSelectOptionSettingsInput.options.map(
                                                  ({ image, ...restOption }) => restOption
                                              ),
                                          }
                                        : singleSelectOptionSettingsInput,
                            },
                        },
                    },
                    variantConfiguratorId,
                },
            });

            const optionBlock = data?.optionBlock;
            // need to save image sequently
            for (const image of sectionImages) {
                if (image instanceof File) {
                    // eslint-disable-next-line no-await-in-loop
                    await uploadAsset(variantConfiguratorId, optionBlock.id, image);
                }
            }

            // handle to save image inside option setting
            // applied for single select for now
            const isSingleSelect = restOptionBlockInput.optionSettings?.type === OptionKind.SingleSelect;
            const isSingleSelectResult = optionBlock?.optionSettings?.__typename === 'SingleSelectOptionSettings';

            if (!isSingleSelect || !isSingleSelectResult) {
                return;
            }

            const optionInputSettings =
                restOptionBlockInput.optionSettings?.singleSelectOptionSettingsInput?.options ?? [];
            const optionResultSettings = optionBlock?.optionSettings?.options ?? [];

            for (let index = 0; index < optionResultSettings.length; index += 1) {
                const input = optionInputSettings?.[index];

                if (input?.image instanceof File) {
                    // eslint-disable-next-line no-await-in-loop
                    await uploadOptionSettingAsset(
                        variantConfiguratorId,
                        optionBlock.id,
                        optionResultSettings[index].id,
                        input.image
                    );
                }
            }
        },
        [createBlockOptionMutation, uploadAsset, uploadOptionSettingAsset]
    );

    const updateBlockOption = useCallback(
        async (variantConfiguratorId: string, optionBlockValue: OptionBlockValue) => {
            const { optionsBlockInput, id } = optionBlockValue;
            const { sectionImages, ...restOptionBlockInput } = optionsBlockInput;
            const { optionSettings } = restOptionBlockInput;

            // delete useless setting
            if (
                !isNil(optionSettings?.singleSelectOptionSettingsInput) &&
                optionSettings.type !== OptionKind.SingleSelect
            ) {
                delete optionSettings?.singleSelectOptionSettingsInput;
            }

            if (
                !isNil(optionSettings?.multiSelectOptionSettingsInput) &&
                optionSettings.type !== OptionKind.MultiSelect
            ) {
                delete optionSettings?.multiSelectOptionSettingsInput;
            }

            if (!isNil(optionSettings?.comboOptionSettingsInput) && optionSettings.type !== OptionKind.Combo) {
                delete optionSettings?.comboOptionSettingsInput;
            }

            const singleSelectOptionSettingsInput = optionSettings?.singleSelectOptionSettingsInput;

            const { data } = await updateBlockOptionMutation({
                variables: {
                    settings: {
                        type: BlockType.Options,
                        optionsBlockInput: {
                            ...restOptionBlockInput,
                            optionSettings: {
                                ...optionSettings,
                                singleSelectOptionSettingsInput:
                                    optionSettings.type === OptionKind.SingleSelect
                                        ? {
                                              ...singleSelectOptionSettingsInput,
                                              options: singleSelectOptionSettingsInput.options.map(
                                                  ({ image, ...restOption }) => restOption
                                              ),
                                          }
                                        : singleSelectOptionSettingsInput,
                            },
                        },
                    },
                    variantConfiguratorId,
                    blockId: id,
                },
            });

            const optionBlock = data?.variantConfigurator?.optionsBlocks?.find(
                block => block.id === optionBlockValue.id
            );

            // Handle section image asset update/create/delete
            const currentSectionImages = optionBlock?.sectionImages ?? [];

            const deleteImagePromises = currentSectionImages?.map(image => {
                const foundImage = sectionImages.find(
                    inputImage => !(inputImage instanceof File) && inputImage?.id === image.id
                );

                if (isNil(foundImage)) {
                    return deleteAsset(variantConfiguratorId, optionBlock.id, image.id);
                }

                return Promise.resolve();
            });

            if (deleteImagePromises.length > 0) {
                await Promise.all(deleteImagePromises);
            }

            // need to save image sequently
            for (const image of sectionImages) {
                if (image instanceof File) {
                    // eslint-disable-next-line no-await-in-loop
                    await uploadAsset(variantConfiguratorId, optionBlock.id, image);
                }
            }

            // Handle option setting image asset update/create/delete
            // applied for single select for now
            const isSingleSelect = restOptionBlockInput.optionSettings?.type === OptionKind.SingleSelect;
            const isSingleSelectResult = optionBlock?.optionSettings?.__typename === 'SingleSelectOptionSettings';

            if (!isSingleSelect || !isSingleSelectResult) {
                return;
            }

            const optionInputSettings =
                restOptionBlockInput.optionSettings?.singleSelectOptionSettingsInput?.options ?? [];
            const optionResultSettings = optionBlock?.optionSettings?.options ?? [];

            for (let index = 0; index < optionResultSettings.length; index += 1) {
                const input = optionInputSettings?.[index];
                const resultSetting =
                    optionResultSettings?.[index].__typename === 'SingleSelectOption'
                        ? optionResultSettings?.[index]
                        : null;

                if (!resultSetting) {
                    continue;
                }

                const resultImage = resultSetting?.__typename === 'SingleSelectOption' ? resultSetting?.image : null;

                if (input?.image instanceof File) {
                    // eslint-disable-next-line no-await-in-loop
                    await uploadOptionSettingAsset(
                        variantConfiguratorId,
                        optionBlock.id,
                        optionResultSettings[index].id,
                        input.image
                    );
                } else if (!input?.image && resultImage?.url) {
                    // eslint-disable-next-line no-await-in-loop
                    await deleteOptionSettingAsset(
                        variantConfiguratorId,
                        optionBlock.id,
                        optionResultSettings[index].id
                    );
                }
            }
        },
        [deleteAsset, deleteOptionSettingAsset, updateBlockOptionMutation, uploadAsset, uploadOptionSettingAsset]
    );

    const deleteBlockOption = useCallback(
        async (variantConfiguratorId: string, blockId: string) => {
            const { data } = await deleteBlockOptionMutation({
                variables: {
                    variantConfiguratorId,
                    blockId,
                },
            });

            return data?.variantConfigurator?.optionsBlocks;
        },
        [deleteBlockOptionMutation]
    );

    return useCallback(
        async (
            blocks: OptionBlockValue[],
            variantConfiguratorId: string,
            initialOptionBlocks: OptionsBlockSpecsFragment[]
        ): Promise<OptionsBlockSpecsFragment[]> => {
            // Delete blocks first
            const deleteBlockPromises = initialOptionBlocks.map(item => {
                const foundBlock = blocks.find(block => !!block?.id && block?.id === item.id);

                if (isNil(foundBlock)) {
                    return deleteBlockOption(variantConfiguratorId, item.id);
                }

                return Promise.resolve();
            });

            if (deleteBlockPromises.length > 0) {
                await Promise.all(deleteBlockPromises);
            }

            const saveBlockPromises = blocks.map(block => {
                if (isNil(block?.id)) {
                    return createBlockOption(variantConfiguratorId, block);
                }

                return updateBlockOption(variantConfiguratorId, block);
            });

            if (saveBlockPromises.length > 0) {
                await Promise.all(saveBlockPromises);
            }

            // intentionally fetch again to get the latest data
            // for form to reset data
            const result = await apollo.query<GetVariantConfiguratorQuery, GetVariantConfiguratorQueryVariables>({
                query: GetVariantConfiguratorDocument,
                variables: {
                    id: variantConfiguratorId,
                },
            });

            return result?.data?.variantConfigurator?.optionsBlocks;
        },
        [apollo, deleteBlockOption, updateBlockOption, createBlockOption]
    );
};

export default useHandleBlockSave;
